import {
  onCleanup,
  batch,
  createEffect,
  createRoot,
  createRenderEffect,
  on,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import {
  register,
  createContext,
  withProvider,
  withConsumer,
  compose,
} from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';
import { animate } from 'https://cdn.jsdelivr.net/npm/motion@10.16.2/+esm';
import { lockyOn } from 'https://cdn.jsdelivr.net/npm/dom-locky@1.0.1/+esm';

function getContext(element) {
  const elementContextSymbol = Object.getOwnPropertySymbols(element).find((s) => s.description === 'element-context');
  const contextSymbol = Object.getOwnPropertySymbols(element[elementContextSymbol]).find(
    (s) => s.description === 'context'
  );
  return element[elementContextSymbol][contextSymbol];
}

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

const animations = {
  backdrop: {
    fadeEnter: (element) => {
      element.style.cssText = `
        display: initial;
        opacity: 0;
      `;
      return animate(element, {
        opacity: [null, 1],
      });
    },
    fadeExit: (element) => {
      const animation = animate(element, {
        opacity: [null, 0],
      });

      animation.finished.then(() => {
        element.style.cssText = `
          display: none;
          opacity: 0;
        `;
      });

      return animation;
    },
  },
};

const MobileNavState = createContext((state = { isOpen: false, isAnimating: false, animationQueue: [] }) => {
  return createStore(state);
});

function MobileNav(props, { element }) {
  const [state, setState] = getContext(element);
  const scrollableElements = element.querySelectorAll('.overflow-y-auto');

  createEffect(() => {
    batch(() => {
      element.setAttribute('is-open', state.isOpen);
      element.setAttribute('is-animating', state.isAnimating);
      setState('isOpen', props.isOpen);
      setState('id', props.id);
    });
  });

  function dispatchStateEvent(action, isOpen) {
    element.dispatchEvent(new CustomEvent(`mobile-nav.${action}`, { detail: { action, isOpen }, bubbles: true }));
  }

  createEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen) dispatchStateEvent('close', isOpen);
        else dispatchStateEvent('open', isOpen);

        dispatchStateEvent('toggle', isOpen);
      }
    )
  );

  const close = () => setState('isOpen', false);
  const open = () => setState('isOpen', true);
  const toggle = () => setState('isOpen', (state) => !state);

  const handleAction = (event) => {
    if (state.isAnimating) return;
    switch (event.detail.action) {
      case 'open':
        return open();
      case 'close':
        return close();
      case 'toggle':
        return toggle();
      default:
        break;
    }
  };

  const handleEsc = (event) => {
    if (event.keyCode !== 27) return;
    return close();
  };

  createRenderEffect(
    on(
      () => state.animationQueue,
      (animationQueue) => {
        if (!animationQueue.length) return;
        const animations = Promise.all([...state.animationQueue]);
        setState('isAnimating', true);
        animations.then(() => {
          batch(() => {
            setState('isAnimating', false);
            setState('animationQueue', []);
          });
        });
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isAnimating,
      (isAnimating) => {
        if (isAnimating || state.isOpen) return;
        scrollableElements.forEach((elem) => {
          elem.scrollTop = 0;
        });
        element.style.display = 'none';
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen) return;
        element.style.display = 'block';
        const unlock = lockyOn(element.closest('header'));
        element.addEventListener('keydown', handleEsc);
        return onCleanup(() => {
          unlock();
          element.removeEventListener('keydown', handleEsc);
        });
      }
    )
  );

  createEffect(() => {
    const id = props.id;
    window.addEventListener(`mobile-nav.${id}`, handleAction);

    onCleanup(() => {
      window.removeEventListener(`mobile-nav.${id}`, handleAction);
    });
  });
}

function MobileNavBackdrop(props, { element, mobileNav }) {
  const [state, setState] = mobileNav;

  element.style.cssText = `
    opacity: 0;
    display: none;
  `;

  const closeModal = () => setState('isOpen', false);
  const updateAnimationQueue = (animation) => setState('animationQueue', (state) => [...state, animation]);

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (isOpen) return;
        const animation = animations.backdrop.fadeExit(element);
        updateAnimationQueue(animation.finished);
        return onCleanup(animation.finish);
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen) return;
        const animation = animations.backdrop.fadeEnter(element);
        updateAnimationQueue(animation.finished);
        return onCleanup(animation.finish);
      }
    )
  );

  createEffect(
    on(
      () => state.isOpen,
      () => {
        element.addEventListener('click', closeModal);

        onCleanup(() => {
          element.removeEventListener('click', closeModal);
        });
      }
    )
  );
}

function MobileNavSection(props, { element, mobileNav }) {
  const [state, setState] = mobileNav;
  const transition = {
    duration: 0.5,
  };

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (!isOpen) return;
        const animation = enter(element);
        updateAnimationQueue(animation.finished);
        return onCleanup(animation.finish);
      }
    )
  );

  createRenderEffect(
    on(
      () => state.isOpen,
      (isOpen) => {
        if (isOpen) return;
        const animation = exit(element);
        updateAnimationQueue(animation.finished);
        return onCleanup(animation.finish);
      }
    )
  );

  function enter(element) {
    return animate(
      element,
      {
        maxHeight: [`var(--initial-max-height)`, `var(--final-max-height)`],
        transform: [`translateX(var(--slide-from))`, `translateX(var(--slide-to))`],
      },
      transition
    );
  }

  function exit(element) {
    return animate(
      element,
      {
        maxHeight: [`var(--final-max-height)`, `var(--initial-max-height)`],
        transform: [`translateX(var(--slide-to))`, `translateX(var(--slide-from))`],
      },
      transition
    );
  }

  function updateAnimationQueue(animation) {
    return setState('animationQueue', (state) => [...state, animation]);
  }
}

function MobileNavTrigger(props, { element }) {
  const dispatchAction = (event) => {
    const customEvent = new CustomEvent(`mobile-nav.${props.target}`, {
      detail: {
        action: props.action,
        currentTarget: event.target,
        relatedTarget: document.getElementById(props.target),
      },
    });
    dispatchEvent(customEvent);
  };

  createEffect(
    on(
      () => ({ target: props.target, action: props.action }),
      ({ target, action }) => {
        element.addEventListener('click', dispatchAction);

        const mobileNav = document.getElementById(target);

        if (mobileNav) {
          function handleStateChange(e) {
            element.setAttribute('is-open', e.detail.isOpen);
          }

          mobileNav.addEventListener(`mobile-nav.${action}`, handleStateChange);

          onCleanup(() => {
            mobileNav.removeEventListener(`mobile-nav.${action}`, handleStateChange);
          });
        }

        onCleanup(() => {
          element.removeEventListener('click', dispatchAction);
        });
      }
    )
  );
}

customShadowlessElement(
  'mobile-nav',
  {
    isOpen: false,
    isAnimating: false,
    id: '',
  },
  MobileNav,
  withProvider(MobileNavState)
);

customShadowlessElement('mobile-nav-backdrop', {}, MobileNavBackdrop, withConsumer(MobileNavState, 'mobileNav'));
customShadowlessElement('mobile-nav-section', {}, MobileNavSection, withConsumer(MobileNavState, 'mobileNav'));

customShadowlessElement('mobile-nav-trigger', { target: '', action: '', isOpen: false }, MobileNavTrigger);
